import { useFavoriteTab } from '../../../hooks/useFavoriteTab';
import React, { useEffect, useState } from 'react';
import databaseMonitorService, { ServerListItem } from '../../../services/databaseMonitor.service';
import ProcessListTab from './ProcessListTab';
import DetailsTab from './DetailsTab';

type TabKey = 'processlist' | 'details';

const DatabaseMonitor: React.FC = () => {
  const [servers, setServers] = useState<ServerListItem[]>([]);
  const [activeServerId, setActiveServerId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('processlist');
  useFavoriteTab(activeTab, setActiveTab, ['processlist', 'details']);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    databaseMonitorService
      .listServers(controller.signal)
      .then((list) => {
        setServers(list);
        setActiveServerId((prev) => prev ?? (list.length ? list[0].id : null));
        setError(null);
        setLoading(false);
      })
      .catch((e: any) => {
        if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
        setError(e?.response?.data?.detail ?? e?.message ?? 'Failed to load servers');
        setLoading(false);
      });
    return () => controller.abort();
  }, []);

  return (
    <div className="p-6 text-slate-900 dark:text-slate-100 flex flex-col gap-4 w-full h-full">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold">Database Monitor</h1>
          {servers.length > 0 && (
            <label className="text-slate-600 dark:text-slate-400 flex items-center gap-2 text-sm">
              Server
              <select
                className="bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-md px-2 py-1 text-sm text-slate-900 dark:text-slate-100"
                value={activeServerId ?? ''}
                onChange={(e) => setActiveServerId(e.target.value || null)}
              >
                {servers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.host}:{s.port})
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>

        {activeServerId && (
          <div className="flex gap-1 border-b border-slate-200 dark:border-slate-800">
            <TabButton active={activeTab === 'processlist'} onClick={() => setActiveTab('processlist')}>
              Processlist
            </TabButton>
            <TabButton active={activeTab === 'details'} onClick={() => setActiveTab('details')}>
              Details
            </TabButton>
          </div>
        )}
      </div>

      {error && (
        <div className="border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-200 rounded-md px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-slate-600 dark:text-slate-400">Loading servers…</div>
      ) : servers.length === 0 && !error ? (
        <div className="border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-200 rounded-md px-4 py-3 text-sm">
          No servers configured. Define servers via the <code className="text-xs">DBMONITOR_*</code>{' '}
          environment variables / KV configuration.
        </div>
      ) : activeServerId ? (
        <div className="w-full flex-1 min-h-0">
          {activeTab === 'processlist' && <ProcessListTab serverId={activeServerId} />}
          {activeTab === 'details' && <DetailsTab serverId={activeServerId} />}
        </div>
      ) : null}
    </div>
  );
};

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={
        'px-3 py-1.5 text-sm rounded-t-md border-b-2 ' +
        (active
          ? 'border-sky-500 text-slate-900 dark:text-slate-100'
          : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200')
      }
    >
      {children}
    </button>
  );
}

export default DatabaseMonitor;
